<template>
  <div>
    <div class="pb-4 print:hidden">
      <MattersFilters
        :filters="filters"
        :loading="loading"
        :active-filter-count="activeFilterCount"
        @apply-filters="loadMatters"
        @clear-filters="handleClearFilters"
      />
    </div>

    <div class="matters-toolbar-sticky" :style="{ top: `${navbarHeight}px` }">
      <BulkActionBar
        v-if="selectedIds.length && permissions?.matters?.create"
        :selected-ids="selectedIds"
        :archived="filters.archived"
        :matter-term="matterTerm"
        :bulk-operation-loading="bulkOperationLoading"
        :can-change-responsible="!!permissions?.matters?.assign"
        :can-apply-rate-group="!!permissions?.matters?.rates && !!permissions?.matters?.assign"
        :can-archive="!!access?.billing"
        @clear="selectedIds = []"
        @change-responsible="confirmChangeResponsible"
        @archive="confirmBulkArchive(!filters.archived)"
        @apply-rate-group="confirmApplyRateGroup"
        @enable-ledes="confirmEnableLedes"
        @disable-ledes="confirmDisableLedes"
      />
      <div v-else class="flex justify-end gap-2 pb-2 print:hidden">
        <v-menu :close-on-content-click="false">
          <template #activator="{ props: menuProps }">
            <LlSecondaryButton
              v-bind="menuProps"
              variant="outlined"
              append-icon="mdi-menu-down"
              data-cy="matters-columns-menu-button"
            >
              Columns
            </LlSecondaryButton>
          </template>
          <v-list density="compact" class="py-0">
            <v-list-item v-if="features?.rateGroups">
              <LlCheckbox
                :model-value="showRateGroupColumn"
                @update:model-value="toggleRateGroupColumn"
                label="Rate Group"
                label-class="text-sm"
                density="compact"
                data-cy="matters-column-rate-group"
              />
            </v-list-item>
            <v-list-item>
              <LlCheckbox
                :model-value="showLedesColumn"
                @update:model-value="toggleLedesColumn"
                label="LEDES"
                label-class="text-sm"
                density="compact"
                data-cy="matters-column-ledes"
              />
            </v-list-item>
          </v-list>
        </v-menu>
        <LlSecondaryButton
          variant="outlined"
          append-icon="mdi-shield-search"
          @click="handleConflictCheck"
        >
          Conflict Check
        </LlSecondaryButton>
        <LlSecondaryButton
          variant="outlined"
          append-icon="mdi-printer"
          @click="handlePrint"
        >
          Print
        </LlSecondaryButton>
        <LlSecondaryButton
          variant="outlined"
          append-icon="mdi-download"
          :disabled="exporting"
          @click="handleExport"
        >
          Export
        </LlSecondaryButton>
        <LlSecondaryButton
          v-if="company?.integration?.quickbooks && access?.billing"
          variant="outlined"
          append-icon="mdi-cloud-sync-outline"
          :loading="loading"
          :disabled="loading"
          @click="handleValidateQuickbooks"
        >
          Validate QuickBooks Connection
        </LlSecondaryButton>
        <LlSecondaryButton color="primary" variant="flat" @click="handleCreateMatter">
          <LlIcon class="mr-1">mdi-plus</LlIcon>
          Create {{ matterTerm }}
        </LlSecondaryButton>
      </div>
    </div>

    <div class="normal-table-container">
      <LlDataTable
        :headers="headers"
        :items="matters"
        :items-length="totalCount"
        :loading="loading"
        :sort-by="sortBy"
        item-value="id"
        class="matters-table"
        show-select
        :model-value="selectedIds"
        :row-props="getRowProps"
        @update:model-value="selectedIds = $event"
        @update:sort-by="handleSortChange"
        @click:row="handleRowClick"
      >
        <!-- QBO Connection Status -->
        <template #header.qbo>
          <LlTooltip
            v-if="company?.integration?.quickbooks && access?.billing"
            text="A green icon in this column shows that matter is connected to QuickBooks and invoices can be generated."
            icon="help"
          />
        </template>
        <template #item.qbo="{ item }">
          <LlIcon
            v-if="filters.checkQuickbooks && item.quickBooksId"
            :icon="item.goodQBConnection ? 'mdi-circle' : 'mdi-circle'"
            :color="item.goodQBConnection ? 'success' : 'error'"
            size="10"
          />
        </template>

        <!-- Matter ID Column -->
        <template #item.reference="{ item }">
          <span class="text-gray-700 text-sm">
            {{ formatOrDefault(item.reference) }}
          </span>
        </template>

        <!-- Matter Column -->
        <template #item.name="{ item }">
          <div class="flex items-center gap-1.5">
            <template v-if="getMatterProcessState(item.id).processing || getMatterProcessState(item.id).error">
              <div class="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <v-progress-circular
                  v-if="getMatterProcessState(item.id).processing"
                  indeterminate
                  size="16"
                  width="2"
                  color="primary"
                />
                <LlTooltip
                  v-else
                  :text="getMatterProcessState(item.id).error"
                  location="top"
                >
                  <template #activator>
                    <LlIcon
                      icon="mdi-alert"
                      size="18"
                      color="warning"
                      class="cursor-help"
                    />
                  </template>
                </LlTooltip>
              </div>
            </template>
            <span class="text-gray-900 text-sm font-medium">
              {{ formatOrDefault(item.name) }}
            </span>
          </div>
        </template>

        <!-- Client ID Column -->
        <template #item.clientReference="{ item }">
          <span class="text-gray-700 text-sm">
            {{ formatOrDefault(item.clientReference) }}
          </span>
        </template>

        <!-- Client Column -->
        <template #item.clientName="{ item }">
          <span class="text-gray-900 text-sm font-medium">
            {{ formatOrDefault(item.clientName) }}
          </span>
        </template>

        <!-- Responsible Column -->
        <template #item.primaryName="{ item }">
          <LlUserAvatar
            v-if="item.primaryName"
            :initials="item.primaryInitials"
            :name="item.primaryName"
          />
          <span v-else class="text-gray-400">—</span>
        </template>

        <!-- Opened Column -->
        <template #item.opened="{ item }">
          <span class="text-gray-700 text-sm">
            {{ getShortDateString(item.opened) }}
          </span>
        </template>

        <!-- Last Activity Column -->
        <template #item.updated="{ item }">
          <span class="text-gray-700 text-sm">
            {{ getShortDateString(item.updated) }}
          </span>
        </template>

        <!-- Billing Type Column -->
        <template #item.billingType="{ item }">
          <span class="text-gray-700 text-sm">
            {{ billingTypeDisplay(item.billingType) }}
          </span>
        </template>

        <!-- Rate Group Column -->
        <template v-if="showRateGroupColumn" #item.rateGroupName="{ item }">
          <LlTooltip v-if="item.rateGroupName" :text="item.rateGroupName" location="top">
            <template #activator>
              <span class="block truncate max-w-[150px] text-gray-900 text-sm">
                {{ item.rateGroupName }}
              </span>
            </template>
          </LlTooltip>
          <span v-else class="text-gray-400 text-sm">Custom rates</span>
        </template>

        <!-- LEDES Column -->
        <template v-if="showLedesColumn" #item.ledesFormat="{ item }">
          <span class="text-gray-700 text-sm">
            {{ item.ledesEnabled ? ledesFormatLabel(item.ledesFormat) : '-' }}
          </span>
        </template>

        <!-- Actions Menu -->
        <template #item.actions="{ item }">
          <v-menu>
            <template #activator="{ props: menuProps }">
              <LlSecondaryButton
                icon="mdi-dots-vertical"
                variant="text"
                size="small"
                density="comfortable"
                :aria-label="`${matterTerm} actions`"
                v-bind="menuProps"
              />
            </template>
            <v-list density="compact">
              <v-list-item @click="viewMatter(item)">
                <v-list-item-title>View {{ matterTerm }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="viewClient(item)">
                <v-list-item-title>View Client</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="company?.integration?.quickbooks && access?.billing"
                @click="openQuickbooksConnector(item)"
              >
                <v-list-item-title>QuickBooks Connector</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.quickBooksId" @click="openTrustSetup(item)">
                <v-list-item-title>Trust Account Setup</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="access?.billing && !item.archived"
                @click="handleArchiveToggle(item, false)"
              >
                <v-list-item-title>Archive</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="access?.billing && item.archived"
                @click="handleArchiveToggle(item, true)"
              >
                <v-list-item-title>Activate</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <!-- Loading State -->
        <template #loading>
          <div class="p-8">
            <v-skeleton-loader type="table-row@10" class="w-full" />
          </div>
        </template>

        <!-- No Data State -->
        <template #no-data>
          <div class="text-center py-16">
            <LlIcon
              icon="mdi-briefcase-outline"
              size="64"
              color="grey-lighten-2"
              class="mb-4"
            />
            <p class="text-xl font-medium text-gray-600 mb-2">No matters found</p>
          </div>
        </template>
      </LlDataTable>
    </div>

    <LlDataTablePagination
      sticky
      class="print:hidden"
      :page="filters.page"
      :page-size="filters.pageSize"
      :total-count="totalCount"
      @update:page="handlePageChange"
      @update:page-size="handlePageSizeChange"
    />

    <LlConfirmationDialog
      ref="archiveConfirmRef"
      :title="`Archive this ${matterTerm}?`"
      :message="archiveConfirmMessage"
      toggle-text="Cancel"
      confirm-text="Archive"
      :show-warning-icon="false"
      preserve-newlines
      confirm-color="primary"
      confirm-variant="flat"
    />

    <!-- Bulk Action Confirmation Dialogs -->
    <LlConfirmationDialog
      ref="changeResponsibleConfirmRef"
      title="Change Responsible"
      :message="changeResponsibleConfirmMessage"
      confirm-text="Change Responsible"
      :show-warning-icon="false"
      :confirm-disabled="!selectedAccountId"
      confirm-color="primary"
      confirm-variant="flat"
    >
      <template #content>
        <LlSelect
          v-model="selectedAccountId"
          :items="getAssignableUsers(accountsList || [])"
          item-title="name"
          item-value="id"
          :return-object="false"
          label="Responsible attorney"
          placeholder="Select an attorney"
          density="compact"
          hide-details
        />
      </template>
    </LlConfirmationDialog>

    <LlConfirmationDialog
      ref="bulkArchiveConfirmRef"
      :title="`${filters.archived ? 'Restore' : 'Archive'} ${matterTerm}s?`"
      :message="bulkArchiveConfirmMessage(!filters.archived)"
      :confirm-text="filters.archived ? 'Restore' : 'Archive'"
      :show-warning-icon="false"
      preserve-newlines
      confirm-color="primary"
      confirm-variant="flat"
    />

    <LlConfirmationDialog
      ref="rateGroupConfirmRef"
      title="Apply Rate Group"
      :message="rateGroupConfirmMessage"
      confirm-text="Apply"
      :show-warning-icon="false"
      :confirm-disabled="!selectedRateGroupId"
      confirm-color="primary"
      confirm-variant="flat"
    >
      <template #content>
        <LlSelect
          v-model="selectedRateGroupId"
          :items="rateGroupList || []"
          item-title="name"
          item-value="rateGroupId"
          :return-object="false"
          label="Rate group"
          placeholder="Select a rate group"
          density="compact"
          hide-details
        />
        <div class="flex mt-4">
          <LlCheckbox
            v-model="selectedUpdateEntries"
            aria-label="Update time entries"
          />
          <div class="pt-1">
            <span class="text-sm">Update time entries</span>
            <span class="block text-xs text-gray-400">
              All WIP time entries for the affected {{ matterTerm }}(s) will be updated to use the rate group's rates.
            </span>
          </div>
        </div>
      </template>
    </LlConfirmationDialog>

    <LlConfirmationDialog
      ref="ledesEnableConfirmRef"
      title="Enable LEDES"
      :message="`${ledesEnableConfirmMessage}\n\nLEDES is configured per-client, so this will apply to every matter sharing a client with your selection - not just the matters selected.`"
      confirm-text="Enable"
      :show-warning-icon="false"
      preserve-newlines
      :confirm-disabled="!selectedLedesFormat"
      confirm-color="primary"
      confirm-variant="flat"
    >
      <template #content>
        <LlSelect
          v-model="selectedLedesFormat"
          :items="ledesFormats"
          item-title="label"
          item-value="id"
          :return-object="false"
          label="LEDES format"
          placeholder="Select a format"
          density="compact"
          hide-details
        />
      </template>
    </LlConfirmationDialog>

    <LlConfirmationDialog
      ref="ledesDisableConfirmRef"
      title="Disable LEDES"
      :message="`${ledesDisableConfirmMessage}\n\nLEDES is configured per-client, so this will apply to every matter sharing a client with your selection - not just the matters selected.`"
      confirm-text="Disable"
      :show-warning-icon="false"
      preserve-newlines
      confirm-color="primary"
      confirm-variant="flat"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import type { PropType } from 'vue';
import { storeToRefs } from 'pinia';
import { useMattersStore } from '@/stores/useMattersStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import LlUserAvatar from '@/components/LlUserAvatar.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlDataTable from '@/components/LlDataTable.vue';
import type { SortByItem } from '@/models/llDataTableTypes';
import LlDataTablePagination from '@/components/LlDataTablePagination.vue';
import MattersFilters from './MattersFilters.vue';
import BulkActionBar from './BulkActionBar.vue';
import { useMattersFilters } from './useMattersFilters';
import { formatOrDefault, getShortDateString, getAssignableUsers } from '@/services/utils';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useExportUtilities } from '@/composables/useExportUtilities';
import { useNavbarHeight } from '@/composables/useNavbarHeight';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useRateGroupStore } from '@/services/useRateGroupStore.js';
import { useMattersBulkOperations } from '@/composables/useMattersBulkOperations';
import RouteService from '@/services/RouteService';
import type { UiRouterState } from '@/services/RouteService/UiRouterState';
import splitBillingService from '@/services/splitBillingService';
import axios from 'axios';

// Only the fields this component reads off a matters/table row.
interface MatterRow {
  id: string;
  clientId: string;
  reference: string;
  name: string;
  clientReference: string;
  clientName: string;
  primaryName: string;
  primaryInitials: string;
  opened: string;
  updated: string;
  billingType: string;
  archived: boolean;
  isSecondarySplitMatter: boolean;
  quickBooksId?: string | null;
  goodQBConnection?: boolean;
  rateGroupName?: string | null;
  ledesEnabled?: boolean;
  ledesFormat?: string | null;
}

const props = defineProps({
  state: { type: Object as PropType<UiRouterState>, required: false },
});

const mattersStore = useMattersStore();
const { matters, totalCount, loading } = storeToRefs(mattersStore);

const meStore = useMeStore();
const { me, access, permissions, company, settings, features } = storeToRefs(meStore);

const { handleSuccess, handleError } = useSnackbarHandler();
const { exportCSV, formatDate } = useExportUtilities();
const routeService = new RouteService(props);
const archiveConfirmRef = ref(null);
const exporting = ref(false);
const selectedIds = ref<string[]>([]);
const navbarHeight = useNavbarHeight();

const matterTerm = computed(() => me.value?.terms?.Matter || 'Matter');

// Rate Group / LEDES column visibility - hidden by default (opt-in via the
// Columns dropdown), persisted the same way Time.vue persists its column
// toggles: a debounced PUT /me carrying a settings.matterTableColumns blob,
// rather than a dedicated columns endpoint (Fixed Fees' heavier pattern -
// not warranted for just two fixed toggles).
const showRateGroupColumn = ref(false);
const showLedesColumn = ref(false);
let columnSettingsUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

const initializeColumnVisibilitySettings = () => {
  const matterTableColumns = settings.value?.matterTableColumns || {};
  // Force off regardless of the persisted value if the company no longer has
  // the rateGroups feature (e.g. a downgrade after the column was toggled on) -
  // the Columns dropdown already hides the checkbox for these companies, so a
  // stale true here would otherwise keep requesting a column nothing can show.
  showRateGroupColumn.value = features.value?.rateGroups ? (matterTableColumns.showRateGroup ?? false) : false;
  showLedesColumn.value = matterTableColumns.showLedes ?? false;
};

const persistColumnVisibilitySettings = () => {
  if (columnSettingsUpdateTimeout) {
    clearTimeout(columnSettingsUpdateTimeout);
  }
  columnSettingsUpdateTimeout = setTimeout(async () => {
    await meStore.updateMe({
      settings: {
        matterTableColumns: {
          showRateGroup: showRateGroupColumn.value,
          showLedes: showLedesColumn.value,
        },
      },
    });
    // No try/catch: useMeStore.updateMe() catches its own errors (shows a
    // snackbar) and never rethrows, so a try/catch here would never run.
    // It also only $patches `settings` from the server response on success,
    // never on failure - so settings.value already IS the correct source of
    // truth either way by the time this resolves, and re-reading it here
    // both confirms a success and reverts a failed optimistic update.
    initializeColumnVisibilitySettings();
  }, 300);
};

const toggleRateGroupColumn = (value: boolean) => {
  showRateGroupColumn.value = value;
  // Unlike LEDES (always present in the response, just conditionally
  // displayed), RateGroupName is now only fetched when showRateGroup is
  // true - toggling this column on/off has to reload, or the column would
  // appear with blank data (turning it on) or keep paying for the join one
  // request longer than necessary (turning it off).
  loadMatters();
  persistColumnVisibilitySettings();
};

const toggleLedesColumn = (value: boolean) => {
  showLedesColumn.value = value;
  persistColumnVisibilitySettings();
};

const { filters, queryParams, activeFilterCount, resetFilters } = useMattersFilters();

const handleClearFilters = () => {
  resetFilters();
  loadMatters();
};

// Any change to the filter criteria itself (search, archived, involvement
// scope, QuickBooks check, custom fields) can change which matters are
// in the result set, so a stale selection could silently keep referencing
// matters no longer shown - clear it. Paging/sorting only reorders/pages
// through the SAME result set, so selection is intentionally preserved
// across those (destructure them out rather than listing filter fields
// explicitly, so new filters added later are covered automatically).
const filterSignature = computed(() => {
  const { page, pageSize, orderBy, sortAscending, ...filterFields } = filters.value;
  return JSON.stringify(filterFields);
});

watch(filterSignature, () => {
  selectedIds.value = [];
});

const accountsStore = useAccountsStore();
const { accountsList } = storeToRefs(accountsStore);
const rateGroupStore = useRateGroupStore();
const { rateGroupList } = storeToRefs(rateGroupStore);

const ledesFormats = [
  { id: 'ledes98', label: 'LEDES 1998b' },
  { id: 'ledes2000', label: 'LEDES 2000' },
  { id: 'ledesLss', label: 'LEDES LSS' },
  { id: 'ledesXml2', label: 'LEDES XML 2.0' },
];

const {
  bulkOperationLoading,
  changeResponsibleConfirmRef,
  archiveConfirmRef: bulkArchiveConfirmRef,
  rateGroupConfirmRef,
  ledesEnableConfirmRef,
  ledesDisableConfirmRef,
  selectedAccountId,
  selectedRateGroupId,
  selectedUpdateEntries,
  selectedLedesFormat,
  changeResponsibleConfirmMessage,
  archiveConfirmMessage: bulkArchiveConfirmMessage,
  rateGroupConfirmMessage,
  ledesEnableConfirmMessage,
  ledesDisableConfirmMessage,
  confirmChangeResponsible,
  confirmArchive: confirmBulkArchive,
  confirmApplyRateGroup,
  confirmEnableLedes,
  confirmDisableLedes,
  getMatterProcessState,
  getRowClass,
} = useMattersBulkOperations({
  getMatterIds: () => selectedIds.value,
  clearSelection: () => {
    selectedIds.value = [];
  },
  onAfterOperation: async () => {
    await loadMatters();
  },
  getMatterTerm: () => matterTerm.value,
});

const getRowProps = ({ item }: { item: MatterRow }) => ({
  class: getRowClass(item.id),
});

// Matches matters-next: the QBO status column (icon + header tooltip) only
// exists at all while the connection-check filter is actually applied, not
// just hidden/blank - otherwise there'd be a permanently empty column.
const headers = computed(() => [
  ...(filters.value.checkQuickbooks
    ? [{
      title: '',
      value: 'qbo',
      sortable: false,
      width: 30,
      // Connection status is meaningless on a printed page (no live QBO
      // data), so drop the column entirely rather than print empty cells.
      headerProps: { class: 'print:hidden' },
      cellProps: { class: 'print:hidden' },
    }]
    : []),
  { title: 'Matter ID', value: 'reference', sortable: true, width: 90 },
  { title: 'Matter', value: 'name', sortable: true, width: 220 },
  { title: 'Client ID', value: 'clientReference', sortable: true, width: 90 },
  { title: 'Client', value: 'clientName', sortable: true, width: 200 },
  { title: 'Responsible', value: 'primaryName', sortable: true, width: 160 },
  { title: 'Opened', value: 'opened', sortable: true, width: 100 },
  { title: 'Last Activity', value: 'updated', sortable: true, width: 100 },
  { title: 'Billing', value: 'billingType', sortable: true, width: 100 },
  ...(showRateGroupColumn.value
    ? [{ title: 'Rate Group', value: 'rateGroupName', sortable: true, width: 160 }]
    : []),
  ...(showLedesColumn.value
    ? [{ title: 'LEDES', value: 'ledesFormat', sortable: true, width: 100 }]
    : []),
  { title: '', value: 'actions', sortable: false, width: 50 },
]);

// Sort keys that differ between the v-data-table header value (used for
// cell rendering) and the legacy backend orderBy param name it must send.
const ORDER_BY_MAP: Record<string, string> = {
  reference: 'matterId',
  primaryName: 'primary',
};

const sortBy = computed<SortByItem[]>(() => [
  {
    key: Object.keys(ORDER_BY_MAP).find((k) => ORDER_BY_MAP[k] === filters.value.orderBy) || filters.value.orderBy,
    order: filters.value.sortAscending ? 'asc' : 'desc',
  },
]);

const handleSortChange = (newSort: SortByItem[]) => {
  if (!newSort || newSort.length === 0) return;
  const key = newSort[0].key;
  filters.value.orderBy = ORDER_BY_MAP[key] || key;
  filters.value.sortAscending = newSort[0].order === 'asc';
  filters.value.page = 1;
  loadMatters();
};

const BILLING_TYPE_LABELS: Record<string, string> = {
  hours: 'Hourly',
  none: 'Pro Bono',
  fixed: 'Fixed Fee',
  contingency: 'Contingency',
  internal: 'Internal',
};

const billingTypeDisplay = (billingType: string) => BILLING_TYPE_LABELS[billingType] || '';

// Matches ClientLedes.vue's `formats` list (id -> full label) - the table
// column shows a short form instead of the full label to keep the column narrow.
const LEDES_FORMAT_LABELS: Record<string, string> = {
  ledes98: '98B',
  ledes2000: '2000',
  ledesLss: 'LSS',
  ledesXml2: 'XML',
};

const ledesFormatLabel = (format: string | null | undefined) => (format ? LEDES_FORMAT_LABELS[format] || format : '');

const loadMatters = () => {
  // knownCount lets the backend skip re-running COUNT(*) on pages after the
  // first (matches legacy buildQuery()'s same optimization).
  const params = { ...queryParams.value };
  if (filters.value.page > 1) {
    params.knownCount = totalCount.value;
  }
  // Lets the backend skip the RateGroups join/column entirely when this page
  // isn't displaying it - both when the company lacks the feature (the
  // Columns dropdown already keeps this false in that case, see
  // initializeColumnVisibilitySettings) and when a user with the feature
  // simply hasn't toggled the column on.
  params.showRateGroup = showRateGroupColumn.value;
  return mattersStore.listMatters(params);
};

const handlePageChange = (page: number) => {
  filters.value.page = page;
  loadMatters();
};

const handlePageSizeChange = (pageSize: number) => {
  filters.value.pageSize = pageSize;
  filters.value.page = 1;
  loadMatters();
};

const handleCreateMatter = () => {
  // Matches legacy redirectToCreate(): accounts on the new time-entry
  // experience get the Vue create-matter modal, everyone else the legacy
  // form. Both are child states of `matters` itself (an `edit` named view
  // layered on top of this page) so the URL stays under /matters instead of
  // navigating to matters-next.
  if (features.value?.timeEntriesTab && settings.value?.enableNewTimeEntry) {
    routeService.go('matters.matter2', { methodOverride: 'matter page' });
    return;
  }
  routeService.go('matters.newMatter', { methodOverride: 'matter page' });
};

const handlePrint = () => {
  window.print();
};

const handleConflictCheck = () => {
  routeService.go('conflict');
};

// Regression to matters-next's old behavior: the per-matter QuickBooks
// connection check is opt-in and explicitly triggered from this button (not
// automatically run whenever the QB Connection dropdown - hidden until this
// has run at least once - is touched). See MattersFilters.vue's checkQuickbooks
// gate on that dropdown.
const handleValidateQuickbooks = () => {
  filters.value.checkQuickbooks = true;
  filters.value.page = 1;
  loadMatters();
};

const handleExport = async () => {
  exporting.value = true;
  try {
    const response = await axios({
      method: 'GET',
      url: '/matters/table',
      params: {
        ...queryParams.value,
        limit: totalCount.value,
        offset: 0,
        showRateGroup: showRateGroupColumn.value,
      },
    });
    await exportCSV(
      response.data.items || [],
      {
        'Matter Id': 'reference',
        Matter: 'name',
        'Client Id': 'clientReference',
        Client: 'clientName',
        Responsible: 'primaryName',
        'Last Activity': (item: MatterRow) => formatDate(item.updated),
        Opened: (item: MatterRow) => formatDate(item.opened),
        Billing: (item: MatterRow) => billingTypeDisplay(item.billingType),
        // Only exported when the user has actually opted into seeing them via
        // the Columns dropdown - matches what's currently shown in the table.
        ...(showRateGroupColumn.value
          ? { 'Rate Group': (item: MatterRow) => item.rateGroupName || 'Custom rates' }
          : {}),
        ...(showLedesColumn.value
          ? { LEDES: (item: MatterRow) => (item.ledesEnabled ? ledesFormatLabel(item.ledesFormat) : '-') }
          : {}),
      },
      { filename: 'Matters' },
    );
  } catch (error) {
    handleError(error);
  } finally {
    exporting.value = false;
  }
};

const viewMatter = (item: MatterRow) => {
  // Matches matters-next's showMatter(matter, true): $state.go('.client.matter', ...)
  // redirects to .client.matter.info (the Info tab), not Overview.
  routeService.go('matters-next.client.matter.info', {
    id: item.clientId,
    matterId: item.id,
  });
};

// Matches matters-next's row behavior: every cell navigates to the matter
// except genuinely interactive elements (the kebab menu button now, the
// selection checkboxes once bulk actions land).
const handleRowClick = (event: MouseEvent, { item }: { item: MatterRow }) => {
  const target = event.target as HTMLElement;
  const isActionClick =
    target.closest('button') ||
    target.closest('.v-selection-control') ||
    target.closest('a') ||
    target.closest('.v-checkbox') ||
    target.closest('.v-data-table__td--select');

  if (isActionClick) return;

  viewMatter(item);
};

const viewClient = (item: MatterRow) => {
  routeService.go('clients.client.view.overview', { id: item.clientId });
};

const openQuickbooksConnector = (item: MatterRow) => {
  routeService.go('matters.connect', { matterId: item.id });
};

const openTrustSetup = (item: MatterRow) => {
  routeService.go('matters.trust', { matterId: item.id });
};

const archiveConfirmMessage = computed(
  () =>
    `Archiving the ${matterTerm.value} will hide it from all users and hide any unbilled time or expenses from some pages. You can easily restore the ${matterTerm.value} from here if you need it again.`,
);

const handleArchiveToggle = async (item: MatterRow, activate: boolean) => {
  if (item.isSecondarySplitMatter) {
    await splitBillingService.showActionRestrictionIfNeeded(
      item.id,
      matterTerm.value,
      activate ? 'Restore' : 'Archive',
    );
    return;
  }

  if (!activate) {
    const confirmed = await archiveConfirmRef.value?.open();
    if (!confirmed) return;
  }

  try {
    await axios.put(`/matters/${item.id}`, { active: activate });
    handleSuccess(
      activate
        ? `The ${matterTerm.value} was restored.`
        : `The ${matterTerm.value} was archived. You can find it on the lists by selecting 'Show Archived'.`,
    );
    await loadMatters();
  } catch (error) {
    handleError(error);
  }
};

onMounted(async () => {
  // Must run before loadMatters() - showRateGroupColumn now determines the
  // showRateGroup request param, so initializing it after the first load
  // would send that request with the wrong value (always false) and leave
  // the Rate Group column visible but blank until a later reload.
  initializeColumnVisibilitySettings();
  loadMatters();
  if (!accountsList.value?.length) {
    await accountsStore.listAccounts();
  }
  if (!rateGroupList.value?.length) {
    await rateGroupStore.listRateGroup();
  }
});

onBeforeUnmount(() => {
  if (columnSettingsUpdateTimeout) {
    clearTimeout(columnSettingsUpdateTimeout);
  }
});
</script>

<style scoped>
.matters-table :deep(.v-data-table__th) {
  background-color: #fafafa !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.8125rem;
  color: #4b5563;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.matters-table :deep(.v-data-table__td) {
  border-bottom: 1px solid #f3f4f6;
  padding: 14px 16px;
  font-size: 0.8125rem;
  color: #111827;
}

.matters-table :deep(tr:hover) {
  background-color: #f9fafb !important;
  cursor: pointer;
}

/* Sticks the bulk-action bar / normal button row to the top of the page's
   scroll container (.leanlaw-main). `top` is set inline via navbarHeight -
   the app's nav bar (header-component .header) is position:fixed, so it
   doesn't reserve space in .leanlaw-main's layout; without this offset,
   `top: 0` would land behind it. */
.matters-toolbar-sticky {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fff;
}

@media print {
  .matters-toolbar-sticky {
    position: static;
    background: none;
  }

  /* v-data-table clips/truncates its scroll wrapper when printing unless
     it's explicitly told to expand to its natural height (same fix as
     FixedFeesTable.vue). */
  .normal-table-container,
  .matters-table :deep(.v-table__wrapper),
  .matters-table :deep(.v-data-table) {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    width: 100% !important;
  }

  /* Selection checkboxes are only useful for on-screen bulk actions - the
     select column has no header key of its own to target, but it's always
     the first column since show-select is unconditional on this table. */
  .matters-table :deep(.v-data-table__th:first-child),
  .matters-table :deep(.v-data-table__td:first-child) {
    display: none !important;
  }
}
</style>
